import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DocumentFilters, SearchScope, DocumentCategory } from '@nvgc/shared';

interface UseDocumentFiltersOptions {
  syncWithUrl?: boolean;
  defaultFilters?: Partial<DocumentFilters>;
}

interface UseDocumentFiltersReturn {
  filters: DocumentFilters;
  setFilters: (filters: DocumentFilters) => void;
  updateFilters: (updates: Partial<DocumentFilters>) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

const DEFAULT_FILTERS: DocumentFilters = {
  categories: [],
  documentSource: 'all'
};

export const useDocumentFilters = (
  options: UseDocumentFiltersOptions = {}
): UseDocumentFiltersReturn => {
  const { syncWithUrl = true, defaultFilters = {} } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  
  const initialFilters: DocumentFilters = {
    ...DEFAULT_FILTERS,
    ...defaultFilters
  };

  const [filters, setFiltersState] = useState<DocumentFilters>(initialFilters);

  // Parse filters from URL parameters
  const parseFiltersFromUrl = useCallback((): DocumentFilters => {
    if (!syncWithUrl) return initialFilters;

    const urlFilters: DocumentFilters = { ...initialFilters };

    // Parse categories
    const categoriesParam = searchParams.get('categories');
    if (categoriesParam) {
      try {
        const categories = categoriesParam.split(',').filter(Boolean) as DocumentCategory[];
        urlFilters.categories = categories;
      } catch (error) {
        console.warn('Failed to parse categories from URL:', error);
      }
    }

    // Parse document source
    const sourceParam = searchParams.get('source');
    if (sourceParam && ['public', 'private', 'all'].includes(sourceParam)) {
      urlFilters.documentSource = sourceParam as SearchScope;
    }

    // Parse date range
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');
    if (startDateParam && endDateParam) {
      try {
        const startDate = new Date(startDateParam);
        const endDate = new Date(endDateParam);
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          urlFilters.dateRange = { start: startDate, end: endDate };
        }
      } catch (error) {
        console.warn('Failed to parse date range from URL:', error);
      }
    }

    return urlFilters;
  }, [searchParams, syncWithUrl, initialFilters]);

  // Update URL parameters from filters
  const updateUrlFromFilters = useCallback((newFilters: DocumentFilters) => {
    if (!syncWithUrl) return;

    const newSearchParams = new URLSearchParams(searchParams);

    // Update categories
    if (newFilters.categories.length > 0) {
      newSearchParams.set('categories', newFilters.categories.join(','));
    } else {
      newSearchParams.delete('categories');
    }

    // Update document source
    if (newFilters.documentSource !== 'all') {
      newSearchParams.set('source', newFilters.documentSource);
    } else {
      newSearchParams.delete('source');
    }

    // Update date range
    if (newFilters.dateRange) {
      newSearchParams.set('startDate', newFilters.dateRange.start.toISOString());
      newSearchParams.set('endDate', newFilters.dateRange.end.toISOString());
    } else {
      newSearchParams.delete('startDate');
      newSearchParams.delete('endDate');
    }

    // Only update if parameters have changed
    const currentParams = searchParams.toString();
    const newParams = newSearchParams.toString();
    if (currentParams !== newParams) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, syncWithUrl]);

  // Initialize filters from URL on mount
  useEffect(() => {
    if (syncWithUrl) {
      const urlFilters = parseFiltersFromUrl();
      setFiltersState(urlFilters);
    }
  }, []); // Only run on mount

  // Update filters and URL
  const setFilters = useCallback((newFilters: DocumentFilters) => {
    setFiltersState(newFilters);
    updateUrlFromFilters(newFilters);
  }, [updateUrlFromFilters]);

  // Partially update filters
  const updateFilters = useCallback((updates: Partial<DocumentFilters>) => {
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);
  }, [filters, setFilters]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
  }, [setFilters, initialFilters]);

  // Check if any filters are active
  const hasActiveFilters = useCallback(() => {
    return (
      filters.categories.length > 0 ||
      filters.documentSource !== 'all' ||
      Boolean(filters.dateRange)
    );
  }, [filters]);

  // Count active filters
  const activeFilterCount = useCallback(() => {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.documentSource !== 'all') count++;
    if (filters.dateRange) count++;
    return count;
  }, [filters]);

  return {
    filters,
    setFilters,
    updateFilters,
    clearFilters,
    hasActiveFilters: hasActiveFilters(),
    activeFilterCount: activeFilterCount()
  };
};

// Hook for managing filter presets
export interface FilterPreset {
  id: string;
  name: string;
  description: string;
  filters: DocumentFilters;
  isDefault?: boolean;
}

interface UseFilterPresetsReturn {
  presets: FilterPreset[];
  savePreset: (name: string, description: string, filters: DocumentFilters) => void;
  deletePreset: (id: string) => void;
  applyPreset: (preset: FilterPreset) => void;
}

export const useFilterPresets = (
  setFilters: (filters: DocumentFilters) => void
): UseFilterPresetsReturn => {
  const [presets, setPresets] = useState<FilterPreset[]>([]);

  // Load presets from localStorage on mount
  useEffect(() => {
    const savedPresets = localStorage.getItem('nvgc-filter-presets');
    if (savedPresets) {
      try {
        setPresets(JSON.parse(savedPresets));
      } catch (error) {
        console.error('Failed to load filter presets:', error);
      }
    }
  }, []);

  // Save presets to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('nvgc-filter-presets', JSON.stringify(presets));
  }, [presets]);

  const savePreset = useCallback((name: string, description: string, filters: DocumentFilters) => {
    const newPreset: FilterPreset = {
      id: `preset-${Date.now()}`,
      name,
      description,
      filters,
      isDefault: false
    };
    setPresets(prev => [...prev, newPreset]);
  }, []);

  const deletePreset = useCallback((id: string) => {
    setPresets(prev => prev.filter(preset => preset.id !== id));
  }, []);

  const applyPreset = useCallback((preset: FilterPreset) => {
    setFilters(preset.filters);
  }, [setFilters]);

  return {
    presets,
    savePreset,
    deletePreset,
    applyPreset
  };
};

// Utility functions for filter validation and transformation
export const validateFilters = (filters: Partial<DocumentFilters>): DocumentFilters => {
  const validatedFilters: DocumentFilters = {
    categories: Array.isArray(filters.categories) ? filters.categories : [],
    documentSource: ['public', 'private', 'all'].includes(filters.documentSource as string) 
      ? (filters.documentSource as SearchScope) 
      : 'all'
  };

  // Validate date range
  if (filters.dateRange && filters.dateRange.start && filters.dateRange.end) {
    const start = new Date(filters.dateRange.start);
    const end = new Date(filters.dateRange.end);
    
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && start <= end) {
      validatedFilters.dateRange = { start, end };
    }
  }

  return validatedFilters;
};

export const serializeFilters = (filters: DocumentFilters): string => {
  return JSON.stringify({
    ...filters,
    dateRange: filters.dateRange ? {
      start: filters.dateRange.start.toISOString(),
      end: filters.dateRange.end.toISOString()
    } : undefined
  });
};

export const deserializeFilters = (serialized: string): DocumentFilters => {
  try {
    const parsed = JSON.parse(serialized);
    return validateFilters({
      ...parsed,
      dateRange: parsed.dateRange ? {
        start: new Date(parsed.dateRange.start),
        end: new Date(parsed.dateRange.end)
      } : undefined
    });
  } catch (error) {
    console.error('Failed to deserialize filters:', error);
    return DEFAULT_FILTERS;
  }
};